import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { renderToString } from 'react-dom/server';
import styled from 'styled-components';
import AdStateContainerRecord from 'PreviewRecords/AdStateContainerRecord';
import HTMLTag from 'RenderComponents/HTMLTag/HTMLTag';
import { LoadingSpinner } from 'PreviewComponents/ResizableContainer/LoadingSpinner';
import { getMetadata, getAdId, getAdStates } from 'Selectors';
import { getActivePageLayout, getContainerGeometry, getIsDevice } from 'PreviewSelectors';
import { updateAdStateContainerDimensions, updateAdStateContainerDragPosition } from 'PreviewActions/customLayoutActions';
import { colourMasterLighter } from 'Styles/colors.css';
import {
  APP,
  PRESENT,
  PREVIEW,
  WIDTH,
} from 'SharedConstants/';

const AdStateContainerDiv = styled.div`
  position: ${props => (props.revealFormat ? 'relative' : 'absolute')};
  background-color: ${colourMasterLighter};
  ${props => props.revealFormat && { clip: 'rect(0px, auto, auto, 0px)' }};
  ${props => props.revealFormat && { clipPath: 'inset(0px)' }};
  ${props => props.selectedAdStateContainer && { zIndex: '1' }};
  ${props => props.selected && { cursor: 'grabbing' }};
  ${props => props.selected && { pointerEvents: 'all' }};
  ${props => props.selected && { zIndex: '2' }};
`;

const AdStateWrapperrDiv = styled.div`
  width: inherit;
  height: inherit;
  ${props => props.selected && { pointerEvents: 'none' }};
`;

const NoConfiguredArtboard = styled.div`
  width: inherit;
  height: inherit;
`;

const AdFrame = styled.iframe`
  position: ${props => props.revealFormat ? 'fixed' : 'absolute'};
  width: inherit;
  height: inherit;
  ${props => props.selectedAdStateContainer && { opacity: '0.2' }};
  ${props => props.selected && { pointerEvents: 'none' }};
  ${props => props.selected && { cursor: 'grabbing' }};
  ${props => props.selected && { opacity: '1' }};
`;

class LayoutAdStateContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      payload: this.getPayload(),
    };
    this.isDevice = getIsDevice() !== null;
  }
  getPayload() {
    const { adId } = this.props;
    return `/build/ads/${adId}/payload.js?preview=true&cachebuster=${Math.random() * 1e20}`;
  }
  reloadAd = () => {
    const payload = this.getPayload();
    this.setState(() => ({ payload }));
  }
  resizeAd = (e, ui, dimension) => {
    const { adStateContainerKey } = this;
    const { deltaX, deltaY } = ui;
    const {
      activePageLayout,
      preview,
      updateAdStateContainerDimensions,
    } = this.props;

    const adStateContainers = activePageLayout.getAdStateContainers(preview);
    const adStateContainer = adStateContainers.find(adStateContaier => adStateContaier.selected === true);
    const adStateContainerGeometry = getContainerGeometry(adStateContainer);
    const isWidth = dimension === WIDTH;
    const delta = isWidth ? deltaX : deltaY;
    const newValue = adStateContainerGeometry[dimension] + delta;
    updateAdStateContainerDimensions(adStateContainer.key, dimension, newValue);
  };
  handleDrag = (e, ui, dragFinished) => {
    e.stopPropagation();
    const { activePageLayout, preview, updateAdStateContainerDragPosition } = this.props;
    const adStateContainers = activePageLayout.getAdStateContainers(preview);
    const adStateContainer = adStateContainers.find(adStateContaier => adStateContaier.selected === true);
    const { x, y } = ui;
    const deltaX = x - this.startX;
    const deltaY = y - this.startY;
    updateAdStateContainerDragPosition(adStateContainer.key, deltaX, deltaY);
  };
  beginDrag = (e, ui) => {
    this.startX = ui.lastX;
    this.startY = ui.lastY;
    this.axisLock = false;
  };
  handleMouseUp = (e) => {
    e.stopPropagation();
  };
  render() {
    const { isDevice, resizeAd } = this;
    const { payload } = this.state;
    const { adId, adStateContainer, availableArtboards, deviceScale, selectedAdStateContainer, revealFormat } = this.props;
    const adStateContainerGeometry = getContainerGeometry(adStateContainer, deviceScale);
    const key = adStateContainer.key;
    const artboard = adStateContainer.artboard || '';
    const adStateArtboardExists = availableArtboards.includes(artboard);
    const selected = adStateContainer.selected;
    const bodyStyle = 'width: 100vw; height: 100vh; margin: 0; overflow: hidden;';
    const progressCircle = renderToString(<LoadingSpinner isDevice={isDevice} />);
    const payloadHTML = renderToString(<HTMLTag adId={adId} payloadURL={payload} />);
    const htmlTag = `<html><body style="${bodyStyle}">${progressCircle}${payloadHTML}<div style="display:none;" data-artboard="%%ARTBOARD%%"></div></body></html>`;

    if (!adStateArtboardExists) {
      return (
        <AdStateContainerDiv
          data-key={key}
          data-fixed={adStateContainer.isFixed}
          revealFormat={revealFormat}
          style={adStateContainerGeometry}
        />
      );
    }

    return (
      <AdStateContainerDiv
        data-key={key}
        data-fixed={adStateContainer.isFixed}
        revealFormat={revealFormat}
        selectedAdStateContainer={selectedAdStateContainer}
        selected={selected}
        onClick={this.handleMouseUp}
        style={adStateContainerGeometry}
      >
        <AdStateWrapperrDiv selected={selected}>
          <AdStateWrapperrDiv selected={selected}>
            <AdFrame
              revealFormat={revealFormat}
              data-preview-artboard={adStateContainer.artboard}
              dataIvoFrame
              frameBorder="0"
              key={key}
              selectedAdStateContainer={selectedAdStateContainer}
              selected={selected}
              srcDoc={htmlTag.replace('%%ARTBOARD%%', artboard)}
            />
          </AdStateWrapperrDiv>
        </AdStateWrapperrDiv>
      </AdStateContainerDiv>
    );
  }
}

LayoutAdStateContainer.propTypes = {
  adId: PropTypes.number,
  adStateContainer: PropTypes.instanceOf(AdStateContainerRecord).isRequired,
  availableArtboards: PropTypes.arrayOf(PropTypes.string),
  deviceScale: PropTypes.number,
  selectedAdStateContainer: PropTypes.instanceOf(AdStateContainerRecord).isRequired,
  preview: PropTypes.object.isRequired,
  revealFormat: PropTypes.bool,
};

LayoutAdStateContainer.defaultProps = {
  adId: 0,
  adStateContainer: {},
  availableArtboards: [],
  deviceScale: 1,
  selectedAdStateContainer: {},
  revealFormat: false,
};

const mapStateToProps = (state) => {
  const artboards = getAdStates(state);
  return {
    activePageLayout: getActivePageLayout(state),
    availableArtboards: artboards.map(artboard => artboard.name).toIndexedSeq().toArray(),
    adId: getAdId(state),
    preview: state.getIn([APP, PRESENT, PREVIEW]),
    revealFormat: getMetadata(state).get('revealFormat') || getMetadata(state).get('formatName') === 'Scroller',
  };
};

const mapDispatchToProps = dispatch => ({
  updateAdStateContainerDimensions: (adStateContainerKey, dimension, value) =>
    dispatch(updateAdStateContainerDimensions(adStateContainerKey, dimension, value)),
  updateAdStateContainerDragPosition: (adStateContainerKey, x, y) =>
    dispatch(updateAdStateContainerDragPosition(adStateContainerKey, x, y)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LayoutAdStateContainer);



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Preview/components/LayoutAdStateContainer/LayoutAdStateContainer.jsx